/**
 * CosmicMap v5 — The Heart of Enume
 *
 * Interactive constellation map where projects exist as stars
 * connected by lines representing creative lineage.
 *
 * v5 Changes (vs v4):
 *  - FIX: Swapped render order — projects render BEFORE challenges in SVG.
 *    In SVG, later elements sit on top. Previously project nodes' invisible
 *    30px hit areas blocked all mouse events on challenge diamonds clustered
 *    near the center. Now challenges render on top and are fully clickable.
 *
 * v5.1 Fixes:
 *  - FIX: Challenge IDs are namespaced as 'c-{id}' in the highlight set to
 *    prevent integer collisions with project IDs (e.g. challenge.id === 10
 *    was lighting up project with id === 10).
 *  - FIX: renderConnection now resolves challenge sourceKey/targetKey using
 *    the 'c-' prefix so the line between a challenge diamond and its parent
 *    project correctly glows on challenge click.
 *  - FIX: ChallengeNode isDimmed check now uses the namespaced key so
 *    challenges don't incorrectly dim themselves when highlighted.
 *  - FIX: CrossZoneBadge filter uses namespaced keys for challenge connections.
 *
 * v5.2 (October 2026) — links on the map:
 *  - Dotted MENTION lines ([[p:ID]] backlinks) via MentionLine. Always drawn
 *    for the selected project; drawn for everything when showLinks is on.
 *  - USE ECHOES via UseEcho: a faint copy of a node at the edge of each OTHER
 *    zone it is used in, tied home by a hairline. OPT-IN: drawn only while
 *    showEchoes is on (the "Show Use echoes" switch in the filter menu).
 *  - Clicking a project now also lights the projects it mentions and the
 *    projects that mention it (effectiveHighlightSet). Forks, parents and
 *    the scrubber are untouched -- mention neighbors are added on top.
 *  - All three props default to empty/off, so the cohort map and the
 *    landing page are unaffected unless they opt in.
 *
 * Place at: src/components/map/CosmicMap.jsx
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { ZONES, getProjectZone, PROJECT_TYPES } from '../../config/zones';
import { api } from '../../api';

// Extracted sub-components
import ProjectNode from './ProjectNode';
import ChallengeNode from './ChallengeNode';
import ConnectionLine from './ConnectionLine';
import { ProjectHoverCard, ChallengeHoverCard, ConnectionHoverCard } from './HoverCards';
import CollectionGlowLayer from './CollectionGlowLayer';
import MentionLine from './MentionLine';
import UseEcho from './UseEcho';

// Shared constants
import {
  NODE_SIZE, CHALLENGE_SIZE, CHALLENGE_HUB_RADIUS,
  CLICK_THRESHOLD, CLUSTER_ZOOM_THRESHOLD,
  PLUCK_DURATION, BLOOM_RADIUS, BLOOM_INNER,
  MOBILE_BLOOM_HOLD_MS, MOBILE_BLOOM_RADIUS,
  getBloomFactor, ringInflationForZoom,
} from './mapConstants';


// ============================================================================
// SKELETON LOADING STATE
// ============================================================================

const MapSkeleton = ({ width, height }) => {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.35;
  const zoneIds = Object.keys(ZONES);

  return (
    <g className="map-skeleton">
      {zoneIds.map((id, i) => {
        const angle = (i / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
        const lx = cx + Math.cos(angle) * (radius + 60);
        const ly = cy + Math.sin(angle) * (radius + 60);
        const zone = ZONES[id];
        return (
          <g key={id} transform={`translate(${lx}, ${ly})`}>
            <text textAnchor="middle" fill={zone.color} fontSize="11" fontWeight="600" opacity={0.15}>
              {zone.shortLabel}
            </text>
          </g>
        );
      })}

      {zoneIds.map((id, zoneIdx) => {
        const angle = (zoneIdx / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
        const zx = cx + Math.cos(angle) * radius;
        const zy = cy + Math.sin(angle) * radius;
        const zone = ZONES[id];

        return [0, 1, 2].map(n => {
          const nAngle = (n / 3) * Math.PI * 2;
          const nr = 45;
          const nx = zx + Math.cos(nAngle) * nr;
          const ny = zy + Math.sin(nAngle) * nr;
          const delay = (zoneIdx * 3 + n) * 0.12;

          return (
            <g key={`ghost-${id}-${n}`} transform={`translate(${nx}, ${ny})`}>
              <circle r={NODE_SIZE / 2 + 3} fill="none" stroke={zone.color} strokeWidth={1} opacity={0.06}>
                <animate attributeName="opacity" values="0.03;0.1;0.03" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
              </circle>
              <circle r={NODE_SIZE / 2} fill={zone.color} opacity={0.03}>
                <animate attributeName="opacity" values="0.02;0.06;0.02" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
              </circle>
            </g>
          );
        });
      })}

      {zoneIds.map((id, i) => {
        const nextId = zoneIds[(i + 1) % zoneIds.length];
        const a1 = (i / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
        const a2 = ((i + 1) / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
        return (
          <line
            key={`ghost-conn-${id}-${nextId}`}
            x1={cx + Math.cos(a1) * radius} y1={cy + Math.sin(a1) * radius}
            x2={cx + Math.cos(a2) * radius} y2={cy + Math.sin(a2) * radius}
            stroke="#3f3f46" strokeWidth={0.5} strokeDasharray="4 8" opacity={0.15}
          >
            <animate attributeName="opacity" values="0.05;0.15;0.05" dur="3s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
          </line>
        );
      })}

      <circle cx={cx} cy={cy} r={12} fill="none" stroke="#a855f7" strokeWidth={1.5} opacity={0.2}>
        <animate attributeName="r" values="8;20;8" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.3;0.05;0.3" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r={4} fill="#a855f7" opacity={0.3}>
        <animate attributeName="opacity" values="0.2;0.5;0.2" dur="1.5s" repeatCount="indefinite" />
      </circle>
    </g>
  );
};


// ============================================================================
// INLINE CROSS-ZONE CELEBRATION (SVG overlay on connection midpoint)
// ============================================================================

const InlineCelebration = ({ sourceNode, targetNode, onDismiss }) => {
  const [phase, setPhase] = useState('burst');
  const hasNodes = !!(sourceNode && targetNode);

  useEffect(() => {
    if (!hasNodes) return;
    const t1 = setTimeout(() => setPhase('label'), 600);
    const t2 = setTimeout(() => setPhase('fade'), 2800);
    const t3 = setTimeout(() => onDismiss?.(), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [hasNodes, onDismiss]);

  if (!hasNodes) return null;

  const mx = (sourceNode.x + targetNode.x) / 2;
  const my = (sourceNode.y + targetNode.y) / 2;
  const dx = targetNode.x - sourceNode.x;
  const dy = targetNode.y - sourceNode.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const curvature = Math.min(dist * 0.12, 40);
  const nx = -dy / dist * curvature;
  const ny = dx / dist * curvature;
  const cpx = mx + nx;
  const cpy = my + ny;
  const pathD = `M ${sourceNode.x} ${sourceNode.y} Q ${cpx} ${cpy} ${targetNode.x} ${targetNode.y}`;

  const sColor = sourceNode.zone?.color || '#71717a';
  const tColor = targetNode.zone?.color || '#71717a';

  return (
    <g style={{ pointerEvents: 'none' }}>
      {phase === 'burst' && (
        <>
          <circle cx={cpx} cy={cpy} r={5} fill="none" stroke="white" strokeWidth={2} opacity={0.8}>
            <animate attributeName="r" from="5" to="60" dur="0.6s" fill="freeze" />
            <animate attributeName="opacity" from="0.8" to="0" dur="0.6s" fill="freeze" />
            <animate attributeName="stroke-width" from="2" to="0.5" dur="0.6s" fill="freeze" />
          </circle>
          <circle cx={cpx} cy={cpy} r={3} fill="none" stroke={sColor} strokeWidth={1.5} opacity={0.6}>
            <animate attributeName="r" from="3" to="40" dur="0.5s" begin="0.1s" fill="freeze" />
            <animate attributeName="opacity" from="0.6" to="0" dur="0.5s" begin="0.1s" fill="freeze" />
          </circle>
        </>
      )}

      <circle r={4} fill="white" opacity={0}>
        <animateMotion dur="0.5s" repeatCount="1" path={pathD} fill="freeze" />
        <animate attributeName="opacity" values="0;1;0.8;0" dur="0.5s" fill="freeze" />
        <animate attributeName="r" values="2;6;3;0" dur="0.5s" fill="freeze" />
      </circle>

      <circle r={3} fill={tColor} opacity={0}>
        <animateMotion dur="0.4s" repeatCount="1" path={`M ${targetNode.x} ${targetNode.y} Q ${cpx} ${cpy} ${sourceNode.x} ${sourceNode.y}`} fill="freeze" begin="0.15s" />
        <animate attributeName="opacity" values="0;0.8;0" dur="0.4s" fill="freeze" begin="0.15s" />
      </circle>

      {(phase === 'label' || phase === 'fade') && (
        <g
          transform={`translate(${cpx}, ${cpy})`}
          opacity={phase === 'fade' ? 0 : 1}
          style={{ transition: 'opacity 0.6s ease-out' }}
        >
          <rect x={-58} y={-14} width={116} height={28} rx={14} fill="#09090b" stroke={sColor} strokeWidth={1} opacity={0.95} />
          <text textAnchor="middle" dominantBaseline="central" fontSize="10" fontWeight="700" letterSpacing="0.5">
            <tspan fill={sColor}>{sourceNode.zone?.shortLabel}</tspan>
            <tspan fill="#71717a"> {'\u2192'} </tspan>
            <tspan fill={tColor}>{targetNode.zone?.shortLabel}</tspan>
          </text>
          {[0, 1, 2, 3].map(i => {
            const sa = (i / 4) * Math.PI * 2;
            const sr = 22 + Math.random() * 8;
            return (
              <circle key={i} cx={Math.cos(sa) * sr} cy={Math.sin(sa) * sr} r={1.5} fill="white" opacity={0}>
                <animate attributeName="opacity" values="0;0.6;0" dur="1s" begin={`${0.2 + i * 0.15}s`} repeatCount="1" fill="freeze" />
              </circle>
            );
          })}
        </g>
      )}
    </g>
  );
};


// ============================================================================
// SMALL INLINE COMPONENTS
// ============================================================================

const ZoneCluster = ({ zone, x, y, projectCount, onClick }) => {
  const clusterR = Math.min(40, 18 + Math.sqrt(projectCount) * 5);
  return (
    <g transform={`translate(${x}, ${y})`} onClick={(e) => { e.stopPropagation(); onClick?.(zone); }} style={{ cursor: 'pointer' }}>
      <circle r={clusterR + 4} fill="none" stroke={zone.color} strokeWidth={1} opacity={0.2}>
        <animate attributeName="opacity" values="0.15;0.25;0.15" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle r={clusterR} fill={zone.color} opacity={0.08} />
      <circle r={clusterR * 0.6} fill={zone.color} opacity={0.06} />
      <text textAnchor="middle" dominantBaseline="central" fill={zone.color} fontSize="13" fontWeight="700">{projectCount}</text>
      <text y={clusterR + 14} textAnchor="middle" fill={zone.color} fontSize="9" fontWeight="500" opacity={0.7}>{zone.shortLabel}</text>
    </g>
  );
};

const ZoneLabel = ({ zone, x, y, isActive, hasHighlights }) => (
  <g transform={`translate(${x}, ${y})`}
    style={{ opacity: hasHighlights ? (isActive ? 0.8 : 0.12) : (isActive ? 0.6 : 0.3), transition: 'opacity 0.4s', pointerEvents: 'none' }}>
    <text textAnchor="middle" fill={zone.color} fontSize="11" fontWeight="600">{zone.shortLabel}</text>
  </g>
);

const CentralHubLabel = ({ x, y, challengeCount }) => (
  <g transform={`translate(${x}, ${y})`} style={{ pointerEvents: 'none' }}>
    <circle r={CHALLENGE_HUB_RADIUS + 20} fill="url(#hubGradient)" opacity={0.3} />
    <text y={-CHALLENGE_HUB_RADIUS - 15} textAnchor="middle" fill="#f59e0b" fontSize="12" fontWeight="600">Help Wanted</text>
    <text y={-CHALLENGE_HUB_RADIUS - 2} textAnchor="middle" fill="#71717a" fontSize="10">{challengeCount} challenge{challengeCount !== 1 ? 's' : ''}</text>
  </g>
);

const CrossZoneBadge = ({ sourceZone, targetZone, x, y, visible }) => {
  if (!visible || !sourceZone || !targetZone) return null;
  return (
    <g transform={`translate(${x}, ${y})`} style={{ pointerEvents: 'none' }}>
      <rect x={-40} y={-10} width={80} height={20} rx={10} fill="#18181b" stroke={sourceZone.color} strokeWidth={0.5} opacity={0.9} />
      <text textAnchor="middle" dominantBaseline="central" fontSize="8" fontWeight="600">
        <tspan fill={sourceZone.color}>{sourceZone.shortLabel}</tspan>
        <tspan fill="#52525b">{' \u2192 '}</tspan>
        <tspan fill={targetZone.color}>{targetZone.shortLabel}</tspan>
      </text>
    </g>
  );
};

const MobileBloomIndicator = ({ x, y, active, radius }) => {
  if (!active) return null;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle cx={x} cy={y} r={radius} fill="none" stroke="#a855f7" strokeWidth={1} opacity={0.15} strokeDasharray="6 4">
        <animate attributeName="opacity" values="0.08;0.2;0.08" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx={x} cy={y} r={6} fill="#a855f7" opacity={0.25}>
        <animate attributeName="r" values="4;8;4" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.15;0.3;0.15" dur="2s" repeatCount="indefinite" />
      </circle>
    </g>
  );
};


// ============================================================================
// MAIN COSMIC MAP COMPONENT
// ============================================================================

export const CosmicMap = ({
  projects = [],
  challenges = [],
  connections = [],
  selectedProjectId = null,
  selectedChallengeId = null,
  highlightedIds = [],
  multiSelectedIds = [],
  activeZones = [],
  showChallenges = true,
  isMultiSelectMode = false,
  zoom = 1,
  pan = { x: 0, y: 0 },
  onZoomChange,
  onPanChange,
  // Ambient territory text. Optional, opt-in, and used only by the cohort
  // map today. Shape: [{ label: 'Wardrobe', projectIds: [12, 19, 34] }].
  //
  // Rendered like ZoneLabel — soft text floating over the constellation —
  // but positioned at the CENTROID of the projects carrying that label, so
  // a cohort's own taxonomy reads as territory on the map instead of
  // living only in a filter bar.
  //
  // The main map passes nothing and is completely unaffected.
  // Background treatment. 'cosmos' (default) is the starfield every map
  // has always had. 'chalkboard' swaps it for a slate surface with chalk
  // dust — used by the cohort map so a class feels like ITS OWN space.
  //
  // Deliberately ONLY the background. Zone colours, node rings, the
  // cross-zone glow hierarchy and every interaction stay identical, so a
  // student moving between the class map and the global one relearns
  // nothing. See the note where the surface renders.
  surface = 'cosmos',
  territoryLabels = [],
  onProjectClick,
  onProjectDoubleClick,
  onChallengeClick,
  onChallengeDoubleClick,
  onConnectionClick,
  onBackgroundClick,
  isLoading = false,
  lensWarmthMap,
  lensProbeId = null,
  isLensActive = false,
  lensResults = [],
  // -- Collection illumination ------------------------------------
  // Project ids of the currently illuminated collection. Blue reach +
  // glow, rendered by CollectionGlowLayer beneath the nodes. Separate
  // from lens warmth on purpose: blue = IS in the collection, fuchsia
  // = RESEMBLES it. Both can be on screen at once.
  collectionMemberIds = [],
  isCollectionActive = false,
  // -- Links (October 2026) ------------------------------------------
  // mentionEdges: [{id, sourceId, targetId}] from GET /api/mentions/map
  // useEchoes:    [{id, projectId, zoneId, note}] from GET /api/uses/map
  // showLinks:    draw every mention line faintly at all times. When false,
  //               mention lines still appear for the selected project.
  // showEchoes:   echoes are opt-in. False = never drawn, not even for the
  //               selected project. True = all faint, selected one bright.
  mentionEdges = [],
  useEchoes = [],
  showLinks = false,
  showEchoes = false,
  className,
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);

  const [hoveredConnectionId, setHoveredConnectionId] = useState(null);
  const [pluckStates, setPluckStates] = useState({});
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const hoverTimerRef = useRef(null);

  // Tracks whether a node/connection was clicked in the current pointer cycle.
  // When true, handleBackgroundMouseUp skips onBackgroundClick so it doesn't
  // immediately undo the selection that the node click just set.
  const nodeClickedRef = useRef(false);

  // Celebration state
  const [celebratingConnection, setCelebratingConnection] = useState(null);
  const celebratedRef = useRef(new Set());
  const initialLoadDoneRef = useRef(false);
  const prevConnectionCountRef = useRef(0);

  // Proximity bloom state
  const [cursorSVG, setCursorSVG] = useState(null);
  const inputModeRef = useRef('mouse');
  const [mobileBloom, setMobileBloom] = useState(null);
  const mobileBloomTimerRef = useRef(null);

  // ── Presence state: { [projectId]: { isWorking, hasLiveSession } } ────────
  const [presenceMap, setPresenceMap] = useState({});

  // Fetch initial presence on mount + refresh when projects list changes
  useEffect(() => {
    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const res = await api.get('/presence/live');
        if (cancelled) return;
        const map = {};
        (res.data?.presence || []).forEach(p => {
          map[p.projectId] = {
            isWorking: Boolean(p.isWorking),
            hasLiveSession: Boolean(p.hasLiveSession),
          };
        });
        setPresenceMap(map);
      } catch { /* presence is non-critical — fail silently */ }
    };
    fetchPresence();
    // Re-fetch every 60s so late-joiners see updated state
    const interval = setInterval(fetchPresence, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Listen for SSE presence_update events
  useEffect(() => {
    const handleSSE = (e) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        const payload = data?.data || data;
        if (payload?.type !== 'presence_update' || !payload?.project_id) return;
        setPresenceMap(prev => ({
          ...prev,
          [payload.project_id]: {
            isWorking: Boolean(payload.is_working),
            hasLiveSession: Boolean(payload.has_live_session),
          },
        }));
      } catch { /* ignore malformed events */ }
    };
    // The RealtimeContext emits map_update as a native CustomEvent on window
    window.addEventListener('map_update', handleSSE);
    return () => window.removeEventListener('map_update', handleSSE);
  }, []);

  // Measure container
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Edge-weighted starfield
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 180; i++) {
      let x = Math.random() * 100;
      let y = Math.random() * 100;
      const edgeBias = 0.4;
      const xNorm = (x / 50) - 1;
      const yNorm = (y / 50) - 1;
      const xBiased = xNorm + edgeBias * (xNorm * xNorm * xNorm - xNorm);
      const yBiased = yNorm + edgeBias * (yNorm * yNorm * yNorm - yNorm);
      x = (xBiased + 1) * 50;
      y = (yBiased + 1) * 50;
      const distFromCenter = Math.sqrt((x - 50) ** 2 + (y - 50) ** 2) / 50;
      const edgeBrightness = 0.05 + distFromCenter * 0.4;
      arr.push({
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y)),
        size: Math.random() * 1.4 + 0.3,
        opacity: Math.random() * edgeBrightness + 0.03,
        dur: 3 + Math.random() * 6,
        delay: Math.random() * 5,
      });
    }
    return arr;
  }, []);

  // ── Calculate node positions ───────────────────────────────────────────
  const { projectNodes, challengeNodes, zoneLabels, centerX, centerY, projectsByZone, zoneGeometry } = useMemo(() => {
    const { width, height } = containerSize;
    if (!width || !height) return { projectNodes: [], challengeNodes: [], zoneLabels: [], centerX: 0, centerY: 0, projectsByZone: {}, zoneGeometry: {} };

    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) * 0.35;
    const zoneIds = Object.keys(ZONES);
    const zonePositions = {};
    const zoneLabelsArr = [];

    zoneIds.forEach((id, i) => {
      const angle = (i / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
      const x = cx + Math.cos(angle) * radius;
      const y = cy + Math.sin(angle) * radius;
      zonePositions[id] = { x, y };
      const labelRadius = radius + 60;
      zoneLabelsArr.push({ zone: ZONES[id], x: cx + Math.cos(angle) * labelRadius, y: cy + Math.sin(angle) * labelRadius });
    });

    const projByZone = {};
    projects.forEach(p => { const zone = getProjectZone(p); if (!projByZone[zone.id]) projByZone[zone.id] = []; projByZone[zone.id].push(p); });

    // Zoom-reveal: inflate ring spacing as the user zooms in past 1.0.
    // This physically pushes nodes apart in SVG space so crowded clusters
    // separate when the user leans in. At zoom ≤ 1.0 layout is unchanged;
    // at zoom 2.5+ rings are ~3× their default size.
    const inflation = ringInflationForZoom(zoom);
    const MIN_SPACING = 70 * inflation;
    const INNER_RADIUS = 40 * inflation;

    const projNodes = projects.map(project => {
      const zone = getProjectZone(project);
      const zonePos = zonePositions[zone.id] || { x: cx, y: cy };
      const zoneProjects = projByZone[zone.id] || [];
      const idx = zoneProjects.indexOf(project);
      const total = zoneProjects.length;

      let x, y;
      if (total === 1) { x = zonePos.x; y = zonePos.y; }
      else if (total <= 6) {
        const angle = (idx / total) * Math.PI * 2 - Math.PI / 2;
        const ringRadius = Math.max(INNER_RADIUS, (MIN_SPACING * total) / (2 * Math.PI));
        x = zonePos.x + Math.cos(angle) * ringRadius;
        y = zonePos.y + Math.sin(angle) * ringRadius;
      } else {
        const nodesPerRing = 6;
        const ring = Math.floor(idx / nodesPerRing);
        const posInRing = idx % nodesPerRing;
        const nodesInThisRing = Math.min(nodesPerRing, total - ring * nodesPerRing);
        const ringRadius = INNER_RADIUS + ring * MIN_SPACING;
        const angleOffset = ring * 0.3;
        const angle = (posInRing / nodesInThisRing) * Math.PI * 2 - Math.PI / 2 + angleOffset;
        x = zonePos.x + Math.cos(angle) * ringRadius;
        y = zonePos.y + Math.sin(angle) * ringRadius;
      }

      return { id: project.id, type: 'project', project, zone, x, y };
    });

    const challNodes = challenges.map((challenge, idx) => {
      const total = challenges.length;
      let x, y;
      if (total === 1) { x = cx; y = cy; }
      else {
        const angle = (idx / total) * Math.PI * 2 - Math.PI / 2;
        const dist = Math.min(CHALLENGE_HUB_RADIUS * 0.7, 25 + (total > 6 ? (idx % 2) * 25 : 0));
        x = cx + Math.cos(angle) * dist;
        y = cy + Math.sin(angle) * dist;
      }
      return { id: challenge.id, type: 'challenge', challenge, parentProjectId: challenge.project_id || challenge.projectId, x, y };
    });

    // Zone centre + the radius of its outermost ring, for placing Use echoes
    // just outside a zone's cluster. Mirrors the ring math above.
    const zoneGeo = {};
    zoneIds.forEach(id => {
      const n = (projByZone[id] || []).length;
      let outer = 0;
      if (n > 1 && n <= 6) outer = Math.max(INNER_RADIUS, (MIN_SPACING * n) / (2 * Math.PI));
      else if (n > 6) outer = INNER_RADIUS + (Math.ceil(n / 6) - 1) * MIN_SPACING;
      zoneGeo[id] = { ...zonePositions[id], outer, inflation };
    });

    return { projectNodes: projNodes, challengeNodes: challNodes, zoneLabels: zoneLabelsArr, centerX: cx, centerY: cy, projectsByZone: projByZone, zoneGeometry: zoneGeo };
  }, [projects, challenges, containerSize, zoom]);

  // Build node lookup
  const nodeMap = useMemo(() => {
    const map = new Map();
    projectNodes.forEach(n => map.set(`project-${n.id}`, n));
    challengeNodes.forEach(n => map.set(`challenge-${n.id}`, n));
    return map;
  }, [projectNodes, challengeNodes]);

  // Build connections
  const allConnections = useMemo(() => {
    const conns = [...connections];
    const existingPairs = new Set();
    conns.forEach(c => {
      existingPairs.add(`${c.sourceType}-${c.sourceId}::${c.targetType}-${c.targetId}`);
      existingPairs.add(`${c.targetType}-${c.targetId}::${c.sourceType}-${c.sourceId}`);
    });
    challengeNodes.forEach(challNode => {
      if (challNode.parentProjectId) {
        const pairKey = `challenge-${challNode.id}::project-${challNode.parentProjectId}`;
        const reversePairKey = `project-${challNode.parentProjectId}::challenge-${challNode.id}`;
        if (!existingPairs.has(pairKey) && !existingPairs.has(reversePairKey)) {
          conns.push({ id: `challenge-conn-${challNode.id}`, sourceType: 'challenge', sourceId: challNode.id, targetType: 'project', targetId: challNode.parentProjectId, isChallenge: true });
          existingPairs.add(pairKey);
        }
      }
    });
    return conns;
  }, [connections, challengeNodes]);

  // Celebration trigger
  useEffect(() => {
    if (!allConnections.length || !projectNodes.length) return;
    const currentCount = allConnections.length;
    if (!initialLoadDoneRef.current) {
      allConnections.forEach(conn => celebratedRef.current.add(conn.id));
      prevConnectionCountRef.current = currentCount;
      initialLoadDoneRef.current = true;
      return;
    }
    if (currentCount <= prevConnectionCountRef.current) return;
    prevConnectionCountRef.current = currentCount;
    for (const conn of allConnections) {
      if (celebratedRef.current.has(conn.id)) continue;
      const sourceNode = nodeMap.get(`${conn.sourceType}-${conn.sourceId}`);
      const targetNode = nodeMap.get(`${conn.targetType}-${conn.targetId}`);
      if (!sourceNode || !targetNode) continue;
      const sZone = sourceNode.zone?.id;
      const tZone = targetNode.zone?.id;
      if (sZone && tZone && sZone !== tZone) {
        celebratedRef.current.add(conn.id);
        if (!celebratingConnection) setCelebratingConnection({ sourceNode, targetNode, connId: conn.id });
        break;
      }
    }
  }, [allConnections, nodeMap, celebratingConnection]);

  const showClusters = zoom < CLUSTER_ZOOM_THRESHOLD && projects.length > 30;

  const { sameZoneConns, crossZoneConns } = useMemo(() => {
    const same = [];
    const cross = [];
    allConnections.forEach(conn => {
      const sourceNode = nodeMap.get(`${conn.sourceType}-${conn.sourceId}`);
      const targetNode = nodeMap.get(`${conn.targetType}-${conn.targetId}`);
      if (!sourceNode || !targetNode) return;
      const sZone = sourceNode.zone?.id;
      const tZone = targetNode.zone?.id;
      const isCrossZone = sZone && tZone && sZone !== tZone;
      const resolved = { ...conn, sourceNode, targetNode, isCrossZone };
      if (isCrossZone) cross.push(resolved);
      else same.push(resolved);
    });
    return { sameZoneConns: same, crossZoneConns: cross };
  }, [allConnections, nodeMap]);

  // Resolve hovered connection for the tooltip card
  const hoveredConnection = useMemo(() => {
    if (!hoveredConnectionId) return null;
    const all = [...sameZoneConns, ...crossZoneConns];
    const conn = all.find(c => c.id === hoveredConnectionId);
    if (!conn || !conn.sourceNode || !conn.targetNode) return null;
    const dx = conn.targetNode.x - conn.sourceNode.x;
    const dy = conn.targetNode.y - conn.sourceNode.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const curvature = Math.min(dist * 0.12, 40);
    const mx = (conn.sourceNode.x + conn.targetNode.x) / 2 + (-dy / dist * curvature);
    const my = (conn.sourceNode.y + conn.targetNode.y) / 2 + (dx / dist * curvature);
    const screenX = mx * zoom + pan.x;
    const screenY = my * zoom + pan.y;
    return { connection: conn, screenX, screenY };
  }, [hoveredConnectionId, sameZoneConns, crossZoneConns, zoom, pan]);

  const hasHighlights = highlightedIds.length > 0;
  const highlightSet = useMemo(() => new Set(highlightedIds), [highlightedIds]);
  const multiSelectSet = useMemo(() => new Set(multiSelectedIds), [multiSelectedIds]);

  // ── Links: mention lines + Use echoes ─────────────────────────────────
  // Mentions whose both ends are on screen. Filtered views and the cohort
  // map therefore only ever draw lines between projects they show.
  const resolvedMentions = useMemo(() => {
    const out = [];
    for (const e of mentionEdges) {
      if (e.sourceId === e.targetId) continue;
      const sourceNode = nodeMap.get(`project-${e.sourceId}`);
      const targetNode = nodeMap.get(`project-${e.targetId}`);
      if (!sourceNode || !targetNode) continue;
      out.push({
        id: `m-${e.id}`, sourceNode, targetNode,
        isCrossZone: sourceNode.zone?.id !== targetNode.zone?.id,
      });
    }
    return out;
  }, [mentionEdges, nodeMap]);

  // project id -> Set of project ids it mentions or is mentioned by
  const mentionNeighbors = useMemo(() => {
    const map = new Map();
    const add = (a, b) => { if (!map.has(a)) map.set(a, new Set()); map.get(a).add(b); };
    resolvedMentions.forEach(m => { add(m.sourceNode.id, m.targetNode.id); add(m.targetNode.id, m.sourceNode.id); });
    return map;
  }, [resolvedMentions]);

  // The project whose links are "in focus": the selected one, while its
  // lineage is lit. A collection or challenge highlight is not a focus.
  const focusProjectId = (hasHighlights && selectedProjectId != null && highlightSet.has(selectedProjectId))
    ? selectedProjectId : null;

  // Lineage highlight + mention neighbors of the focused project. Used
  // everywhere highlightSet was used for projects and fork lines, so a
  // mentioned project lights up exactly like a parent or child does.
  const effectiveHighlightSet = useMemo(() => {
    const extra = focusProjectId != null ? mentionNeighbors.get(focusProjectId) : null;
    if (!extra || extra.size === 0) return highlightSet;
    const s = new Set(highlightSet);
    extra.forEach(id => s.add(id));
    return s;
  }, [highlightSet, focusProjectId, mentionNeighbors]);

  // One echo per (project, other zone). Placed just outside the other
  // zone's cluster, on the side FACING the project's real node, so the
  // hairline home is short and reads as "this came from over there".
  const echoNodes = useMemo(() => {
    const byKey = new Map();
    for (const u of useEchoes) {
      const homeNode = nodeMap.get(`project-${u.projectId}`);
      const zone = ZONES[u.zoneId];
      if (!homeNode || !zone || zone.id === homeNode.zone?.id) continue;   // home-zone use = the node itself
      const key = `${u.projectId}-${u.zoneId}`;
      if (!byKey.has(key)) byKey.set(key, { key, homeNode, zone, project: homeNode.project, notes: [] });
      if (u.note) byKey.get(key).notes.push(u.note);
    }

    const byZone = new Map();
    for (const echo of byKey.values()) {
      if (!byZone.has(echo.zone.id)) byZone.set(echo.zone.id, []);
      byZone.get(echo.zone.id).push(echo);
    }

    const placed = [];
    for (const [zoneId, list] of byZone) {
      const geo = zoneGeometry?.[zoneId];
      if (!geo || geo.x == null) continue;
      const R = geo.outer + 42 * (geo.inflation || 1);
      const minGap = 34 / Math.max(R, 1);           // ~ echo diameter + breathing room
      list.forEach(e => { e.angle = Math.atan2(e.homeNode.y - geo.y, e.homeNode.x - geo.x); });
      list.sort((a, b) => a.angle - b.angle);
      for (let i = 1; i < list.length; i++) {
        if (list[i].angle - list[i - 1].angle < minGap) list[i].angle = list[i - 1].angle + minGap;
      }
      list.forEach(e => placed.push({ ...e, x: geo.x + Math.cos(e.angle) * R, y: geo.y + Math.sin(e.angle) * R }));
    }
    return placed;
  }, [useEchoes, nodeMap, zoneGeometry]);

  // 'focus' | 'ambient' | 'dimmed' | null (not drawn)
  const linkState = useCallback((touchesFocus) => {
    if (focusProjectId != null) return touchesFocus ? 'focus' : (showLinks ? 'dimmed' : null);
    if (hasHighlights) return showLinks ? 'dimmed' : null;
    return showLinks ? 'ambient' : null;
  }, [focusProjectId, hasHighlights, showLinks]);

  // Echoes only exist while the filter-menu switch is on.
  const echoState = useCallback((touchesFocus) => {
    if (!showEchoes) return null;
    if (focusProjectId != null) return touchesFocus ? 'focus' : 'dimmed';
    if (hasHighlights) return 'dimmed';
    return 'ambient';
  }, [focusProjectId, hasHighlights, showEchoes]);

  const bloomCenter = cursorSVG || mobileBloom;
  const bloomRadius = mobileBloom ? MOBILE_BLOOM_RADIUS : BLOOM_RADIUS;
  const bloomInner = mobileBloom ? 50 : BLOOM_INNER;

  const screenToSVG = useCallback((clientX, clientY) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    return { x: (clientX - rect.left - pan.x) / zoom, y: (clientY - rect.top - pan.y) / zoom };
  }, [pan, zoom]);

  // ── Helper: resolve highlight key for any node ─────────────────────────
  // Challenge nodes are stored in the highlight set as 'c-{id}' to prevent
  // integer collisions with project IDs. Project nodes use their raw id.
  const getHighlightKey = useCallback((node) => {
    if (!node) return null;
    return node.type === 'challenge' ? `c-${node.id}` : node.id;
  }, []);

  // ── Interaction handlers ──────────────────────────────────────────────

  const bloomRafRef = useRef(null);
  const pendingCursorRef = useRef(null);

  const flushCursor = useCallback(() => {
    if (pendingCursorRef.current) { setCursorSVG(pendingCursorRef.current); pendingCursorRef.current = null; }
    bloomRafRef.current = null;
  }, []);

  const handleMouseMove = useCallback((e) => {
    inputModeRef.current = 'mouse';
    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      if (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD) setHasDragged(true);
      onPanChange?.({ x: pan.x + dx, y: pan.y + dy });
      setDragStart({ x: e.clientX, y: e.clientY });
    }
    setMousePos({ x: e.clientX, y: e.clientY });
    const svgPos = screenToSVG(e.clientX, e.clientY);
    if (svgPos) {
      pendingCursorRef.current = svgPos;
      if (!bloomRafRef.current) bloomRafRef.current = requestAnimationFrame(flushCursor);
    }
  }, [isDragging, dragStart, pan, onPanChange, screenToSVG, flushCursor]);

  const handleMouseDown = useCallback((e) => { if (e.button !== 0) return; setIsDragging(true); setDragStart({ x: e.clientX, y: e.clientY }); setHasDragged(false); nodeClickedRef.current = false; }, []);
  const handleMouseUp = useCallback(() => { setIsDragging(false); if (!hasDragged) setHoveredNode(null); }, [hasDragged]);
  const handleBackgroundMouseUp = useCallback(() => { setIsDragging(false); if (!hasDragged && !nodeClickedRef.current) onBackgroundClick?.(); nodeClickedRef.current = false; }, [hasDragged, onBackgroundClick]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    const newZoom = Math.max(0.2, Math.min(3, zoom + delta));
    if (!containerRef.current) { onZoomChange?.(newZoom); return; }
    const rect = containerRef.current.getBoundingClientRect();
    const cursorScreenX = e.clientX - rect.left;
    const cursorScreenY = e.clientY - rect.top;
    const svgX = (cursorScreenX - pan.x) / zoom;
    const svgY = (cursorScreenY - pan.y) / zoom;
    onZoomChange?.(newZoom);
    onPanChange?.({ x: cursorScreenX - svgX * newZoom, y: cursorScreenY - svgY * newZoom });
  }, [zoom, pan, onZoomChange, onPanChange]);

  // Touch handlers
  const touchRef = useRef({ startDist: 0, startZoom: 1, startPan: { x: 0, y: 0 }, midpoint: null, lastTap: 0 });

  const handleTouchStart = useCallback((e) => {
    inputModeRef.current = 'touch';
    setCursorSVG(null);
    if (e.touches.length === 2) {
      const t0 = e.touches[0]; const t1 = e.touches[1];
      const dx = t0.clientX - t1.clientX; const dy = t0.clientY - t1.clientY;
      touchRef.current.startDist = Math.sqrt(dx * dx + dy * dy);
      touchRef.current.startZoom = zoom;
      touchRef.current.startPan = { ...pan };
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        touchRef.current.midpoint = { screenX: (t0.clientX + t1.clientX) / 2 - rect.left, screenY: (t0.clientY + t1.clientY) / 2 - rect.top };
      }
      if (mobileBloomTimerRef.current) { clearTimeout(mobileBloomTimerRef.current); mobileBloomTimerRef.current = null; }
      setMobileBloom(null);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setHasDragged(false);
      const svgPos = screenToSVG(e.touches[0].clientX, e.touches[0].clientY);
      if (svgPos) {
        mobileBloomTimerRef.current = setTimeout(() => { setMobileBloom(svgPos); mobileBloomTimerRef.current = null; }, MOBILE_BLOOM_HOLD_MS);
      }
    }
  }, [zoom, pan, screenToSVG]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (mobileBloomTimerRef.current) { clearTimeout(mobileBloomTimerRef.current); mobileBloomTimerRef.current = null; }
    if (e.touches.length === 2) {
      const t0 = e.touches[0]; const t1 = e.touches[1];
      const dx = t0.clientX - t1.clientX; const dy = t0.clientY - t1.clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = dist / (touchRef.current.startDist || 1);
      const newZoom = Math.max(0.2, Math.min(3, touchRef.current.startZoom * scale));
      const mid = touchRef.current.midpoint;
      if (mid) {
        const svgX = (mid.screenX - touchRef.current.startPan.x) / touchRef.current.startZoom;
        const svgY = (mid.screenY - touchRef.current.startPan.y) / touchRef.current.startZoom;
        onZoomChange?.(newZoom);
        onPanChange?.({ x: mid.screenX - svgX * newZoom, y: mid.screenY - svgY * newZoom });
      } else { onZoomChange?.(newZoom); }
    } else if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - dragStart.x; const dy = e.touches[0].clientY - dragStart.y;
      if (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD) setHasDragged(true);
      onPanChange?.({ x: pan.x + dx, y: pan.y + dy });
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      if (mobileBloom) { const svgPos = screenToSVG(e.touches[0].clientX, e.touches[0].clientY); if (svgPos) setMobileBloom(svgPos); }
    }
  }, [isDragging, dragStart, pan, onPanChange, onZoomChange, mobileBloom, screenToSVG]);

  const handleTouchEnd = useCallback((e) => {
    if (mobileBloomTimerRef.current) { clearTimeout(mobileBloomTimerRef.current); mobileBloomTimerRef.current = null; }
    if (e.touches.length === 0) setMobileBloom(null);
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setCursorSVG(null); pendingCursorRef.current = null;
    if (bloomRafRef.current) { cancelAnimationFrame(bloomRafRef.current); bloomRafRef.current = null; }
    setHoveredNode(null);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const handleConnectionClick = useCallback((conn, sourceNode, targetNode) => {
    nodeClickedRef.current = true;
    const connId = conn.id;
    setPluckStates(prev => ({ ...prev, [connId]: 'plucking' }));
    setTimeout(() => setPluckStates(prev => ({ ...prev, [connId]: 'ringing' })), PLUCK_DURATION * 0.7);
    setTimeout(() => setPluckStates(prev => ({ ...prev, [connId]: null })), PLUCK_DURATION + 600);
    onConnectionClick?.(conn, sourceNode, targetNode);
  }, [onConnectionClick]);

  const handleNodeMouseEnter = useCallback((node, event) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      setHoveredNode({ node, screenX: node.x * zoom + pan.x, screenY: node.y * zoom + pan.y });
    }, 400);
  }, [zoom, pan]);

  const handleNodeMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoveredNode(null);
  }, []);

  // Wrapped click handlers — set nodeClickedRef so background mouseUp
  // doesn't immediately clear the selection that the node click just set.
  const handleProjectNodeClick = useCallback((project, e) => {
    nodeClickedRef.current = true;
    onProjectClick?.(project, e);
  }, [onProjectClick]);

  const handleProjectNodeDoubleClick = useCallback((project) => {
    nodeClickedRef.current = true;
    onProjectDoubleClick?.(project);
  }, [onProjectDoubleClick]);

  const handleChallengeNodeClick = useCallback((challenge) => {
    nodeClickedRef.current = true;
    onChallengeClick?.(challenge);
  }, [onChallengeClick]);

  const handleChallengeNodeDoubleClick = useCallback((challenge) => {
    nodeClickedRef.current = true;
    onChallengeDoubleClick?.(challenge);
  }, [onChallengeDoubleClick]);

  const handleClusterClick = useCallback(() => { onZoomChange?.(1.2); }, [onZoomChange]);

  // ── Render helper ─────────────────────────────────────────────────────

  const renderConnection = useCallback((conn) => {
    // Challenge nodes use 'c-{id}' in the highlight set to avoid integer
    // collisions with project IDs. Resolve the correct key for each end.
    const sourceKey = conn.sourceType === 'challenge'
      ? `c-${conn.sourceNode?.id}`
      : conn.sourceNode?.id;
    const targetKey = conn.targetType === 'challenge'
      ? `c-${conn.targetNode?.id}`
      : conn.targetNode?.id;
    const isHighlighted = effectiveHighlightSet.has(sourceKey) && effectiveHighlightSet.has(targetKey);
    const isDimmed = hasHighlights && !isHighlighted;
    return (
      <ConnectionLine
        key={conn.id} connectionId={conn.id}
        sourceNode={conn.sourceNode} targetNode={conn.targetNode}
        isCrossZone={conn.isCrossZone} isHighlighted={isHighlighted} isDimmed={isDimmed}
        isHovered={hoveredConnectionId === conn.id} pluckState={pluckStates[conn.id]}
        onClick={() => handleConnectionClick(conn, conn.sourceNode, conn.targetNode)}
        onMouseEnter={() => setHoveredConnectionId(conn.id)}
        onMouseLeave={() => setHoveredConnectionId(null)}
      />
    );
  }, [effectiveHighlightSet, hasHighlights, hoveredConnectionId, pluckStates, handleConnectionClick]);

  // ── Main render ───────────────────────────────────────────────────────

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full overflow-hidden',
        surface === 'chalkboard' ? 'bg-[#1b2420]' : 'bg-zinc-950',
        className
      )}
      onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
      onMouseUp={handleBackgroundMouseUp} onMouseLeave={handleMouseLeave}
      onWheel={handleWheel} onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
      style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
    >
      {/* ── Surface ──────────────────────────────────────────────────
          Two treatments, same geometry. The starfield reuses the SAME
          `stars` array for chalk dust rather than generating a second
          one: identical positions, different colour and no twinkle.
          Chalk does not shimmer.

          #1b2420 is a desaturated slate green, chosen because it is dark
          enough that the eight zone colours keep their contrast. A
          brighter or more saturated green would mute amber and indigo,
          and the cross-zone glow — which is the whole visual argument of
          this map — reads as a smudge against it. If you push the
          background lighter, check a cross-zone connection first.        */}
      <div className="absolute inset-0 pointer-events-none">
        {surface === 'chalkboard' ? (
          <>
            {/* Chalk dust: static, warm-white, no animation. */}
            {stars.map((star, i) => (
              <div key={i} className="absolute rounded-full"
                style={{
                  left: `${star.x}%`, top: `${star.y}%`,
                  width: star.size * 1.6, height: star.size * 1.6,
                  backgroundColor: '#e8e6dc',
                  opacity: star.opacity * 0.5,
                  filter: 'blur(0.5px)',
                }} />
            ))}
            {/* Faint smear, as if the board were wiped rather than washed. */}
            <div className="absolute inset-0" style={{
              background:
                'radial-gradient(ellipse 70% 45% at 30% 35%, rgba(232,230,220,0.035), transparent 70%),' +
                'radial-gradient(ellipse 55% 60% at 75% 65%, rgba(232,230,220,0.025), transparent 70%)',
            }} />
            {/* Wooden frame edge — one detail, not a whole skeuomorph. */}
            <div className="absolute inset-0 rounded-xl" style={{
              boxShadow: 'inset 0 0 0 3px rgba(120,88,56,0.35), inset 0 0 40px rgba(0,0,0,0.45)',
            }} />
          </>
        ) : (
          stars.map((star, i) => (
            <div key={i} className="absolute rounded-full bg-white"
              style={{ left: `${star.x}%`, top: `${star.y}%`, width: star.size, height: star.size, opacity: star.opacity, animation: `twinkle ${star.dur}s ease-in-out ${star.delay}s infinite` }} />
          ))
        )}
      </div>

      {/* Mouse-follow glow */}
      {!isDragging && cursorSVG && (
        <div className="absolute pointer-events-none"
          style={{
            left: mousePos.x - (containerRef.current?.getBoundingClientRect()?.left || 0) - 150,
            top: mousePos.y - (containerRef.current?.getBoundingClientRect()?.top || 0) - 150,
            width: 300, height: 300,
            background: 'radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 70%)',
            borderRadius: '50%', transition: 'left 0.1s, top 0.1s',
          }}
        />
      )}

      {/* SVG Map */}
      <svg ref={svgRef} className="absolute inset-0" width={containerSize.width} height={containerSize.height} style={{ overflow: 'visible' }}>
        <defs>
          <filter id="crossGlow"><feGaussianBlur stdDeviation="4" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
          <radialGradient id="hubGradient" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.15} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></radialGradient>
        </defs>

        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {isLoading && containerSize.width > 0 && <MapSkeleton width={containerSize.width / zoom} height={containerSize.height / zoom} />}

          {!isLoading && zoneLabels.map(({ zone, x, y }) => (
            <ZoneLabel key={zone.id} zone={zone} x={x} y={y} isActive={activeZones.length === 0 || activeZones.includes(zone.id)} hasHighlights={hasHighlights} />
          ))}

          {!isLoading && showChallenges && challengeNodes.length > 0 && (
            <CentralHubLabel x={centerX} y={centerY} challengeCount={challengeNodes.length} />
          )}

          {/* ── Territory text ──────────────────────────────────────── */}
          {/* Cohort taxonomy rendered as ambient labels over the clusters
              that carry them. Drawn BEFORE nodes so it sits behind them —
              it is atmosphere, not a layer to click through.

              A label needs at least two projects to earn a position. One
              project is a node, not a territory, and the text would just
              sit on top of it. */}
          {!isLoading && (() => {
            // ── Territory label layout ──────────────────────────────
            // Placement is done as ONE pass over all labels, not per
            // label, because the hard part is collisions rather than
            // position.
            //
            // Two problems the naive version had:
            //
            //  1. OVERLAP. Every label on the same project resolves to
            //     the same centroid, so a project tagged both "costume"
            //     and "makeup" drew two strings on identical coordinates
            //     — illegible, and it looked like a rendering fault.
            //
            //  2. ECHO. A node already prints its project name beneath
            //     it. A territory label saying nearly the same thing
            //     directly above reads as the text being duplicated at
            //     two sizes rather than as two different pieces of
            //     information.
            //
            // So: labels are placed, sorted top-to-bottom, then pushed
            // apart vertically until none are within MIN_GAP of another.
            // Labels that merely echo the single node they sit on are
            // dropped entirely.
            const MIN_GAP = 26;

            const placed = [];
            for (const { label, projectIds } of territoryLabels) {
              if (!label || !Array.isArray(projectIds) || projectIds.length < 1) continue;
              const ids = new Set(projectIds);
              const members = projectNodes.filter(n => ids.has(n.id));
              if (members.length < 1) continue;

              // Drop the echo: one member whose name already says this.
              if (members.length === 1) {
                const nodeName = (members[0].project?.name || '').trim().toLowerCase();
                const labelText = label.trim().toLowerCase();
                if (nodeName === labelText ||
                    nodeName.includes(labelText) ||
                    labelText.includes(nodeName)) {
                  continue;
                }
              }

              placed.push({
                label,
                weight: members.length,
                x: members.reduce((sum, n) => sum + n.x, 0) / members.length,
                // Single-member labels sit ABOVE the node so they never
                // cover the cover image or its name underneath.
                y: members.reduce((sum, n) => sum + n.y, 0) / members.length
                   - (members.length === 1 ? 52 : 0),
              });
            }

            // Push apart. Bigger territories keep their position and the
            // smaller ones move, so the label covering five projects
            // stays anchored to its cluster.
            placed.sort((a, b) => (b.weight - a.weight) || (a.y - b.y));
            for (let i = 1; i < placed.length; i++) {
              for (let j = 0; j < i; j++) {
                const dx = Math.abs(placed[i].x - placed[j].x);
                const dy = Math.abs(placed[i].y - placed[j].y);
                if (dx < 130 && dy < MIN_GAP) {
                  placed[i].y = placed[j].y + (placed[i].y >= placed[j].y ? MIN_GAP : -MIN_GAP);
                  j = -1;   // re-check against everything already placed
                }
              }
            }

            return placed.map(({ label, x, y, weight }) => (
              <text
                key={`territory-${label}`}
                x={x}
                y={y}
                textAnchor="middle"
                fill={surface === 'chalkboard' ? '#e8e6dc' : '#a1a1aa'}
                // Territories covering more projects read slightly
                // larger. A quiet signal of where the class's work is
                // actually concentrated.
                fontSize={surface === 'chalkboard' ? (weight > 1 ? 16 : 13) : (weight > 1 ? 14 : 12)}
                fontWeight={500}
                letterSpacing="0.18em"
                opacity={hasHighlights ? 0.10 : (weight > 1 ? 0.30 : 0.20)}
                style={{
                  pointerEvents: 'none',
                  textTransform: 'uppercase',
                  transition: 'opacity 300ms ease',
                }}
              >
                {label}
              </text>
            ));
          })()}

          {!isLoading && showClusters && Object.entries(projectsByZone).map(([zoneId, zoneProjects]) => {
            const zoneIds = Object.keys(ZONES);
            const i = zoneIds.indexOf(zoneId);
            if (i === -1) return null;
            const angle = (i / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
            const radius = Math.min(containerSize.width, containerSize.height) * 0.35 / zoom;
            const cx = containerSize.width / (2 * zoom);
            const cy = containerSize.height / (2 * zoom);
            return <ZoneCluster key={zoneId} zone={ZONES[zoneId]} x={cx + Math.cos(angle) * radius} y={cy + Math.sin(angle) * radius} projectCount={zoneProjects.length} onClick={handleClusterClick} />;
          })}

          {!isLoading && !showClusters && (
            <>
              {sameZoneConns.map(renderConnection)}
              {crossZoneConns.map(renderConnection)}
              {/* Blue reach for an illuminated collection. Above the fork
                  lines, below the nodes. Decoration only -- clicks pass
                  straight through to the nodes underneath. */}
              <CollectionGlowLayer
                nodes={projectNodes}
                memberIds={collectionMemberIds}
                active={isCollectionActive}
              />

              {/* ── Mention lines (dotted backlinks) ── decoration only */}
              {resolvedMentions.map(m => {
                const state = linkState(m.sourceNode.id === focusProjectId || m.targetNode.id === focusProjectId);
                if (!state) return null;
                return (
                  <MentionLine key={m.id} id={m.id} sourceNode={m.sourceNode} targetNode={m.targetNode}
                    isCrossZone={m.isCrossZone} state={state} />
                );
              })}

              {/* ── Use echoes: a project's ghost in each other zone it is used in.
                  Only while "Show Use echoes" is on in the filter menu.
                  Under the real nodes so a real node always wins the click. */}
              {echoNodes.map(echo => {
                const state = echoState(echo.homeNode.id === focusProjectId);
                if (!state) return null;
                return <UseEcho key={`echo-${echo.key}`} echo={echo} state={state} onClick={handleProjectNodeClick} />;
              })}


              {/* CrossZoneBadge — disabled, ConnectionHoverCard handles this now
              {crossZoneConns
                .filter(c => {
                  const srcKey = c.sourceType === 'challenge' ? `c-${c.sourceNode?.id}` : c.sourceNode?.id;
                  const tgtKey = c.targetType === 'challenge' ? `c-${c.targetNode?.id}` : c.targetNode?.id;
                  return (highlightSet.has(srcKey) && highlightSet.has(tgtKey)) || hoveredConnectionId === c.id;
                })
                .map(conn => {
                  const dx = conn.targetNode.x - conn.sourceNode.x;
                  const dy = conn.targetNode.y - conn.sourceNode.y;
                  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                  const mx = (conn.sourceNode.x + conn.targetNode.x) / 2;
                  const my = (conn.sourceNode.y + conn.targetNode.y) / 2;
                  const nx = -dy / dist * Math.min(dist * 0.12, 40);
                  const ny = dx / dist * Math.min(dist * 0.12, 40);
                  return <CrossZoneBadge key={`badge-${conn.id}`} sourceZone={conn.sourceNode.zone} targetZone={conn.targetNode.zone} x={mx + nx} y={my + ny - 15} visible />;
                })}
              */}

              {mobileBloom && <MobileBloomIndicator x={mobileBloom.x} y={mobileBloom.y} active radius={MOBILE_BLOOM_RADIUS} />}

              {/* Projects render FIRST (underneath) */}
              {projectNodes.map(node => {
                const bf = bloomCenter ? getBloomFactor(node.x, node.y, bloomCenter.x, bloomCenter.y, bloomRadius, bloomInner) : 0;
                return <ProjectNode
                  key={node.id} node={node}
                  isSelected={selectedProjectId === node.id}
                  isHighlighted={effectiveHighlightSet.has(node.id)}
                  isDimmed={hasHighlights ? (!effectiveHighlightSet.has(node.id) && selectedProjectId !== node.id) : (isLensActive && !lensWarmthMap?.has(node.id))}
                  isMultiSelected={multiSelectSet.has(node.id)}
                  isMultiSelectMode={isMultiSelectMode}
                  bloomFactor={bf}
                  zoom={zoom}
                  isWorking={presenceMap[node.id]?.isWorking || node.project?.is_working || false}
                  hasLiveSession={presenceMap[node.id]?.hasLiveSession || false}
                  lensWarmth={isLensActive ? (lensWarmthMap?.get(node.id) || 0) : 0}
                  isLensProbe={isLensActive && node.id === lensProbeId}
                  onClick={handleProjectNodeClick}
                  onDoubleClick={handleProjectNodeDoubleClick}
                  onMouseEnter={handleNodeMouseEnter}
                  onMouseLeave={handleNodeMouseLeave}
                />;
              })}

              {/* Challenges render SECOND (on top — clickable, not blocked by project hit areas) */}
              {showChallenges && challengeNodes.map(node => {
                const bf = bloomCenter ? getBloomFactor(node.x, node.y, bloomCenter.x, bloomCenter.y, bloomRadius, bloomInner) : 0;
                // Use namespaced key 'c-{id}' for both isHighlighted and isDimmed checks
                const challKey = `c-${node.id}`;
                const isHighlighted = highlightSet.has(challKey);
                const isDimmed = hasHighlights && !isHighlighted && selectedChallengeId !== node.id;
                return <ChallengeNode
                  key={node.id} node={node}
                  isSelected={selectedChallengeId === node.id}
                  isHighlighted={isHighlighted}
                  isDimmed={isDimmed}
                  bloomFactor={bf}
                  zoom={zoom}
                  onClick={handleChallengeNodeClick}
                  onDoubleClick={handleChallengeNodeDoubleClick}
                  onMouseEnter={handleNodeMouseEnter}
                  onMouseLeave={handleNodeMouseLeave}
                />;
              })}

              {celebratingConnection && (
                <InlineCelebration sourceNode={celebratingConnection.sourceNode} targetNode={celebratingConnection.targetNode} onDismiss={() => setCelebratingConnection(null)} />
              )}
            </>
          )}
        </g>
      </svg>

      {/* Hover cards (HTML layer) */}
      {hoveredNode && hoveredNode.node.type === 'project' && !selectedProjectId && !isMultiSelectMode && (
        <ProjectHoverCard hoveredNode={hoveredNode} containerSize={containerSize} />
      )}
      {hoveredNode && hoveredNode.node.type === 'challenge' && !selectedChallengeId && !isMultiSelectMode && (
        <ChallengeHoverCard hoveredNode={hoveredNode} containerSize={containerSize} />
      )}
      {hoveredConnection && !selectedProjectId && !selectedChallengeId && !isMultiSelectMode && (
        <ConnectionHoverCard
          connection={hoveredConnection.connection}
          screenX={hoveredConnection.screenX}
          screenY={hoveredConnection.screenY}
          containerSize={containerSize}
        />
      )}

      <style>{`
        @keyframes hoverCardIn { from { opacity: 0; transform: translateY(4px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes twinkle { 0%, 100% { opacity: var(--tw-opacity, 0.1); } 50% { opacity: calc(var(--tw-opacity, 0.1) * 2.5); } }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>

      {isMultiSelectMode && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 px-4 py-2 bg-emerald-500/20 border border-emerald-500/50 backdrop-blur rounded-full">
          <span className="text-sm text-emerald-400 font-medium">Multi-select: tap projects to select</span>
        </div>
      )}
    </div>
  );
};

export default CosmicMap;